import { sanitizeToFragment } from './sanitize';

/**
 * What a completed comparison answer says about its result (spec §4,
 * chat-runs-api.md §3.5): the model marks "no differences" explicitly only
 * after a complete comparison, and marks documents or parts it could not
 * compare as partial or unavailable, never as no differences. The UI tells
 * these apart from a table of differences, and from an answer without a
 * table (which is not a usable comparison result either).
 */

export type ComparisonOutcome =
  | 'differences'
  | 'no_differences'
  /** At least one row is partial or unavailable. */
  | 'incomplete'
  | 'no_table';

const normalized = (text: string) =>
  text.replace(/\s+/g, ' ').trim().toLowerCase();

const NO_DIFFERENCES = new Set(['no differences', 'no difference', '差異なし']);
const INCOMPLETE = new Set(['partial', 'unavailable', '一部のみ', '比較不可']);

export const comparisonOutcome = (answerHtml: string): ComparisonOutcome => {
  const fragment = sanitizeToFragment(answerHtml);
  const table = fragment.querySelector('table');
  if (!table) return 'no_table';
  const rows = Array.from(table.querySelectorAll('tr')).filter(row =>
    row.querySelector('td'),
  );
  if (rows.length === 0) return 'no_table';
  let incomplete = false;
  let noDifferenceRows = 0;
  for (const row of rows) {
    const cells = Array.from(row.querySelectorAll('td')).map(cell =>
      normalized(cell.textContent ?? ''),
    );
    if (cells.some(cell => INCOMPLETE.has(cell))) incomplete = true;
    else if (cells.some(cell => NO_DIFFERENCES.has(cell))) {
      noDifferenceRows += 1;
    }
  }
  if (incomplete) return 'incomplete';
  return noDifferenceRows === rows.length ? 'no_differences' : 'differences';
};
